// src/App.tsx
import React, { useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import "./index.css";
import { Navigation } from "./components/Navigation";
import { LandingPage } from "./components/LandingPage";
import ChatPage from "./components/ChatPage";
import DashboardPage from "./components/Dashboard";
import { SignInPage } from "./components/auth/SignInPage";
import { SignUpPage } from "./components/auth/SignUpPage";
import { useAuth } from "./hooks/useAuth";
import { supabase } from "./lib/supabase/browserClient";
import { Sparkles } from "lucide-react";

// Feature components
import { MirrorSession } from "./components/onboarding/MirrorSession";
import { SoulFileDashboard } from "./components/soul-file/SoulFileDashboard";
import { OracleCardPull } from "./components/oracle/OracleCardPull";
import { DailyAlignment } from "./components/rituals/DailyAlignment";
import { WeeklyRitual } from "./components/rituals/WeeklyRitual";
import { AffirmationGenerator } from "./components/affirmations/AffirmationGenerator";
import { GratitudeJournal } from "./components/gratitude/GratitudeJournal";
import { EvidenceBank } from "./components/evidence/EvidenceBank";
import { VisionBoard } from "./components/vision-board/VisionBoard";
import { GuidedVisualization } from "./components/visualizations/GuidedVisualization";
import { MicroChallenges } from "./components/challenges/MicroChallenges";

/* ---------- Ensure Soul File Exists ---------- */
async function ensureSoulFileExists(userId: string): Promise<boolean> {
  try {
    const { data: existing, error: checkError } = await supabase
      .from('soul_files')
      .select('id, onboarding_completed')
      .eq('user_id', userId)
      .single();

    if (existing) return existing.onboarding_completed || false;

    if (checkError?.code === 'PGRST116') {
      await supabase.from('soul_files').insert({ user_id: userId });
      await supabase.from('user_essence').insert({ user_id: userId, display_name: '', preferred_name: '' });
      await supabase.from('mystara_notes').insert({ user_id: userId });
      await supabase.from('user_streaks').insert({ user_id: userId });
      return false;
    }
    return true;
  } catch (err) {
    console.error('[App] Error:', err);
    return true;
  }
}

/* ---------- Onboarding Gate ---------- */
function OnboardingGate({ children }: { children: React.ReactNode }) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    async function checkOnboarding() {
      if (authLoading) return;
      if (!user) { setChecking(false); return; }
      if (location.pathname === '/onboarding' || location.pathname.startsWith('/auth') || location.pathname === '/') {
        setChecking(false);
        return;
      }

      try {
        const completed = await ensureSoulFileExists(user.id);
        if (!completed) navigate('/onboarding');
      } catch (err) {
        console.error(err);
      } finally {
        setChecking(false);
      }
    }
    checkOnboarding();
  }, [user, authLoading, navigate, location.pathname]);

  if (checking && user && !location.pathname.startsWith('/auth') && location.pathname !== '/') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <Sparkles className="w-12 h-12 text-purple-500 animate-pulse" />
      </div>
    );
  }
  return <>{children}</>;
}

/* ---------- Protected Route ---------- */
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!loading && !user) navigate('/auth/sign-in');
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-pink-50 via-purple-50 to-blue-50">
        <Sparkles className="w-12 h-12 text-purple-500 animate-pulse" />
      </div>
    );
  }
  if (!user) return null;
  return <>{children}</>;
}

/* ---------- Page Wrappers ---------- */
function OnboardingPage() {
  const navigate = useNavigate();
  return <MirrorSession onComplete={() => setTimeout(() => navigate('/chat'), 500)} />;
}

function OraclePage() {
  const navigate = useNavigate();
  return <OracleCardPull onClose={() => navigate('/chat')} />;
}

function AlignmentPage({ type }: { type: 'morning' | 'evening' }) {
  const navigate = useNavigate();
  return <DailyAlignment type={type} onComplete={() => navigate('/chat')} onClose={() => navigate('/chat')} />;
}

function WeeklyRitualPage({ type }: { type: 'reset' | 'check-in' }) {
  const navigate = useNavigate();
  return <WeeklyRitual type={type} onComplete={() => navigate('/chat')} onClose={() => navigate('/chat')} />;
}

/* ---------- Shell ---------- */
function Shell() {
  const { user } = useAuth();
  const location = useLocation();

  const hideNavRoutes = ['/', '/chat', '/onboarding', '/oracle', '/align', '/ritual', '/affirmations', '/gratitude', '/evidence', '/vision-board', '/visualizations', '/challenges'];
  const shouldHideNav = hideNavRoutes.some(route => location.pathname.startsWith(route));
  
  const wantsAuthShell = location.pathname.startsWith("/chat") || location.pathname.startsWith("/dashboard");
  const shellClass = user && wantsAuthShell ? "app-auth bg-mystara min-h-screen antialiased" : "min-h-screen";
  
  return (
    <div className={shellClass}>
      {!shouldHideNav && <Navigation />}
      
      <main className={user && wantsAuthShell ? "text-mystara-onglass" : ""}>
        <OnboardingGate>
          <Routes>
            {/* Public */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth/sign-in" element={<SignInPage />} />
            <Route path="/auth/sign-up" element={<SignUpPage />} />
            
            {/* Onboarding */}
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            
            {/* Main App */}
            <Route path="/chat" element={<ProtectedRoute><ChatPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            
            {/* Soul File - redirect to chat with soul-file view */}
            <Route path="/soul-file" element={<Navigate to="/chat?view=soul-file" replace />} />
            <Route path="/profile" element={<Navigate to="/chat?view=soul-file" replace />} />
            
            {/* Oracle */}
            <Route path="/oracle" element={<ProtectedRoute><OraclePage /></ProtectedRoute>} />
            
            {/* Daily Rituals */}
            <Route path="/align/morning" element={<ProtectedRoute><AlignmentPage type="morning" /></ProtectedRoute>} />
            <Route path="/align/evening" element={<ProtectedRoute><AlignmentPage type="evening" /></ProtectedRoute>} />
            
            {/* Weekly Rituals */}
            <Route path="/ritual/reset" element={<ProtectedRoute><WeeklyRitualPage type="reset" /></ProtectedRoute>} />
            <Route path="/ritual/check-in" element={<ProtectedRoute><WeeklyRitualPage type="check-in" /></ProtectedRoute>} />
            
            {/* Features - Easy */}
            <Route path="/affirmations" element={<ProtectedRoute><AffirmationGenerator /></ProtectedRoute>} />
            <Route path="/gratitude" element={<ProtectedRoute><GratitudeJournal /></ProtectedRoute>} />
            <Route path="/evidence" element={<ProtectedRoute><EvidenceBank /></ProtectedRoute>} />
            
            {/* Features - Medium */}
            <Route path="/vision-board" element={<ProtectedRoute><VisionBoard /></ProtectedRoute>} />
            <Route path="/visualizations" element={<ProtectedRoute><GuidedVisualization /></ProtectedRoute>} />
            <Route path="/challenges" element={<ProtectedRoute><MicroChallenges /></ProtectedRoute>} />
            
            {/* Fallback */}
            <Route path="*" element={<LandingPage />} />
          </Routes>
        </OnboardingGate>
      </main>
    </div>
  );
}

/* ---------- App ---------- */
export default function App() {
  return (
    <BrowserRouter>
      <div className="nosedive">
        <Shell />
      </div>
    </BrowserRouter>
  );
}
